import React, { FormEvent, useState } from 'react';
import axios from 'axios';
import { useModalManager } from '@/components/common/Modal';
import SubmitModal from '@/components/ui/Modal/SubmitModal';
import Select from '@/components/common/ReactSelect5';
import LessonConfirmation from '@/components/admin/RefMatTaggedContentTable/LessonConfirmation';
import { plato_api_reference_material_model_reference_materials_path } from '@/modules/routes';
import { queryClient } from '@/utils/ReactQuery';
import { useMutation } from '@tanstack/react-query';
import { useReferenceMaterialLessons } from '@/components/admin/RefMatTaggedContentTable/useReferenceMaterialLessons';
import { useLessons, usePrograms } from '@/hooks/api/v2';

interface AddTaggedContentButtonProps {
  referenceMaterial: {
    id: number;
    title: string;
  };
}

export const AddTaggedContentButton = ({ referenceMaterial }: AddTaggedContentButtonProps) => {
  const [programId, setProgramId] = useState<number>();
  const [lessonId, setLessonId] = useState<number>();
  const { close, isOpen, open } = useModalManager();

  const { data: programs, isLoading } = usePrograms({
    extra_attributes: ['lesson_ids'],
    only: ['full_title_with_edition', 'id', 'code'],
    params: { order: 'title_en', page: 1, per_page: 500 },
  });

  const { data: lessons, isLoading: isLessonsLoading } = useLessons(programId, {
    enabled: !!programId,
    extra_attributes: ['program_ids'],
    only: ['id', 'unit_id', 'title_with_position', 'image_url', 'title_number', 'title'],
  });

  const { data: referenceMaterialLessons } = useReferenceMaterialLessons(referenceMaterial.id);

  const closeModal = () => {
    close();
    setProgramId(null);
    setLessonId(null);
  };

  const { isLoading: isSubmitting, mutate } = useMutation({
    mutationFn: () => (
      axios
        .post(
          plato_api_reference_material_model_reference_materials_path(referenceMaterial.id),
          { model_id: lessonId, model_type: 'Lesson', reference_material_id: referenceMaterial.id },
        )
        .finally(async () => {
          await queryClient.invalidateQueries(['lessons', 'referenceMaterial', referenceMaterial.id]);
          closeModal();
        })
    ),
    mutationKey: ['modelReferenceMaterial', referenceMaterial.id],
  });

  const programOptions = programs.map(program => ({
    label: `[${program.code}] ${program.full_title_with_edition}`,
    value: program.id,
  }));

  const currentLessonIds = referenceMaterialLessons.map(lesson => lesson.id);

  const lessonOptions = lessons
    .filter(lesson => !currentLessonIds.includes(lesson.id))
    .map(lesson => ({
      label: lesson.title_with_position,
      value: lesson.id,
    }));

  const lesson = lessons.find(l => l.id === lessonId);
  const cancelHandler = lesson ? () => setLessonId(null) : close;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    mutate();
  };

  return (
    <>
      <button className="btn btn--sm btn--green" type="button" onClick={open}>Add Association</button>
      <SubmitModal
        allowOverflow
        cancel={cancelHandler}
        className="tw-text-base"
        close={closeModal}
        headerText="Add Association"
        isLoading={isSubmitting}
        isOpen={isOpen}
        onSubmit={handleSubmit}
      >
        {!lesson && (
          <>
            <p className="tw-my-3">Select a program:</p>
            <div className="tw-ml-3">
              <label className="tw-mb-2 tw-font-bold" htmlFor="program-select">Program</label>
              <Select
                isClearable
                className="tw-text-sm"
                id="program-select"
                isLoading={isLoading}
                options={programOptions}
                placeholder="Select or search for a program..."
                value={programOptions.find(opt => opt.value === programId)}
                onChange={(option: { label: string, value: number }) => setProgramId(option ? option.value : undefined)}
              />
            </div>
            <p className="tw-mt-8 tw-mb-3">
              Then select the lesson you want to associate
              {' '}
              <span className="tw-font-bold">{referenceMaterial.title}</span>
              {' '}
              with:
            </p>
            <div className="tw-ml-3">
              <label className="tw-mb-2 tw-font-bold" htmlFor="lesson-select">Lesson</label>
              <Select
                className="tw-text-sm"
                id="lesson-select"
                isDisabled={!programId || lessons.length === 0 || isLessonsLoading}
                isLoading={isLoading}
                options={lessonOptions}
                placeholder={programId ? 'Select or search for a lesson...' : 'Select a program first...'}
                value={lessonOptions.find(opt => opt.value === lessonId)}
                onChange={(option: { label: string, value: number }) => setLessonId(option.value)}
              />
            </div>
          </>
        )}
        {lesson && <LessonConfirmation lesson={lesson} referenceMaterial={referenceMaterial} />}
      </SubmitModal>
    </>
  );
};
